import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Switch, 
  FormControlLabel, 
  Button, 
  Box, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { format } from 'date-fns';

import offlineApi from '../services/offlineApi';

const OfflineMode = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [pendingOperations, setPendingOperations] = useState([]);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [showOfflineDetails, setShowOfflineDetails] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    severity: 'info',
    message: ''
  });

  // Load initial state
  useEffect(() => {
    const loadOfflineState = async () => {
      try {
        const isEnabled = await offlineApi.isOfflineModeEnabled();
        setEnabled(isEnabled);
        await loadStats();
        setLoading(false);
      } catch (error) {
        console.error('Error loading offline state:', error);
        setLoading(false);
      }
    };

    loadOfflineState();

    // Listen for online/offline events
    const handleOnline = () => setNetworkStatus(true);
    const handleOffline = () => setNetworkStatus(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load statistics about offline storage
  const loadStats = async () => {
    try {
      const offlineStats = await offlineApi.getOfflineStats();
      setStats(offlineStats);
      
      const operations = await offlineApi.getPendingOperations();
      setPendingOperations(operations);
    } catch (error) {
      console.error('Error loading offline stats:', error);
    }
  };

  // Toggle offline mode
  const toggleOfflineMode = async (event) => {
    const newState = event.target.checked;
    setEnabled(newState);
    
    try {
      // First set the mode
      await offlineApi.setOfflineMode(newState);
      
      // If enabling, trigger prefetch
      if (newState) {
        setSyncing(true);
        
        try {
          // Force a full prefetch of data
          console.log('Starting full data prefetch for offline mode...');
          const result = await offlineApi.prefetchEssentialData();
          console.log('Prefetch completed successfully:', result);
          
          // Show success feedback
          setAlert({
            show: true,
            severity: 'success',
            message: 'Data successfully cached for offline use!'
          });
        } catch (prefetchError) {
          console.error('Error prefetching data:', prefetchError);
          setAlert({
            show: true,
            severity: 'warning',
            message: 'Some data may not be available offline. Try again when you have better connectivity.'
          });
        } finally {
          setSyncing(false);
        }
      } else {
        // Show disabled message
        setAlert({
          show: true,
          severity: 'info',
          message: 'Offline mode disabled. You need an internet connection to use the app.'
        });
      }
      
      await loadStats();
    } catch (error) {
      console.error('Error toggling offline mode:', error);
      setEnabled(!newState); // Revert UI state on error
      setAlert({
        show: true,
        severity: 'error',
        message: 'Failed to change offline mode setting.'
      });
    }
  };

  // Sync with server
  const handleSync = async () => {
    setSyncing(true);
    try {
      // Force a more aggressive sync attempt that prioritizes pending deletes
      const result = await offlineApi.forceSyncWithServer();
      await loadStats();
      
      // Immediately check if we still have pending operations after sync
      const operations = await offlineApi.getPendingOperations();
      
      if (operations.length > 0) {
        // If we still have pending operations, try one more forced sync
        console.log('Still have pending operations after sync, trying one more time...');
        await offlineApi.forceSyncWithServer(true); // true = forcePriority for deletes
        await loadStats(); // Refresh stats again
      }
      
      // Show success message
      setAlert({
        show: true,
        severity: 'success',
        message: 'Synchronization completed successfully!'
      });
      
      return result;
    } catch (error) {
      console.error('Error syncing with server:', error);
      
      // Show error message
      setAlert({
        show: true,
        severity: 'error',
        message: 'Failed to synchronize with server. Please try again later.'
      });
      
      return false;
    } finally {
      setSyncing(false);
    }
  };

  // Clear all offline data
  const handleClearData = async () => {
    setShowConfirmClear(false);
    setLoading(true);
    
    try {
      await offlineApi.clearOfflineData();
      await loadStats();
    } catch (error) {
      console.error('Error clearing offline data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Retry a failed operation
  const handleRetryOperation = async (id) => {
    try {
      await offlineApi.retryOperation(id);
      await loadStats();
    } catch (error) {
      console.error('Error retrying operation:', error);
    }
  };

  // Delete a pending operation
  const handleDeleteOperation = async (id) => {
    try {
      await offlineApi.deleteOperation(id);
      await loadStats();
    } catch (error) {
      console.error('Error deleting operation:', error);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch (error) {
      return dateStr;
    }
  };

  // Get operation status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Chip size="small" label="Pending" color="primary" />;
      case 'syncing':
        return <Chip size="small" label="Syncing" color="info" icon={<SyncIcon />} />;
      case 'error':
        return <Chip size="small" label="Error" color="error" icon={<ErrorIcon />} />;
      case 'requires_action':
        return <Chip size="small" label="Action Needed" color="warning" icon={<WarningIcon />} />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  // Get operation description
  const getOperationDescription = (operation) => {
    const { method, endpoint } = operation;
    
    if (method === 'post' && endpoint === '/items/upload') {
      return 'Upload new item (waiting for connectivity)';
    }
    
    if (method === 'post' && endpoint.startsWith('/tags/update/')) {
      return `Update tags for item ${endpoint.split('/').pop()}`;
    }
    
    if (method === 'put' && endpoint.startsWith('/items/')) {
      return `Update item ${endpoint.split('/').pop()}`;
    }
    
    if (method === 'put' && endpoint.startsWith('/boxes/')) {
      return `Update box ${endpoint.split('/').pop()}`;
    }
    
    if (method === 'delete' && endpoint.startsWith('/items/')) {
      return `Delete item ${endpoint.split('/').pop()}`;
    }
    
    if (method === 'delete' && endpoint.startsWith('/boxes/')) {
      return `Delete box ${endpoint.split('/').pop()}`;
    }
    
    return `${method.toUpperCase()} ${endpoint}`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" component="h2">
          Offline Mode
          {!networkStatus && (
            <Tooltip title="You are currently offline">
              <CloudOffIcon color="warning" sx={{ ml: 1, verticalAlign: 'middle' }} />
            </Tooltip>
          )}
        </Typography>
        
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={toggleOfflineMode}
                color="primary"
              />
            }
            label={enabled ? "Enabled" : "Disabled"}
          />
        )}
      </Box>

      <Typography variant="body2" color="textSecondary" paragraph>
        Offline mode allows you to view boxes and items when you don't have internet access.
        Changes made while offline will sync when you reconnect.
      </Typography>

      {alert.show && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlert({...alert, show: false})}
        >
          {alert.message}
        </Alert>
      )}

      {enabled && stats && (
        <>
          <Box my={2}>
            <Typography variant="subtitle2">Storage Status</Typography>
            <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
              <Chip label={`${stats.boxCount} Boxes`} variant="outlined" />
              <Chip label={`${stats.itemCount} Items`} variant="outlined" />
              <Chip 
                label={`${stats.pendingRequestCount} Pending Operations`} 
                color={stats.pendingRequestCount > 0 ? "warning" : "success"}
                variant="outlined" 
              />
              <Chip 
                label={`Last sync: ${formatDate(stats.lastSyncTime)}`} 
                variant="outlined" 
                icon={<CloudDoneIcon />}
              />
            </Box>
          </Box>

          <Box display="flex" gap={1} mt={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleSync}
              disabled={syncing || !networkStatus}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            
            <Button
              variant="outlined"
              color="info"
              onClick={() => setShowOfflineDetails(true)}
            >
              View Details
            </Button>
            
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setShowConfirmClear(true)}
            >
              Clear Data
            </Button>
          </Box>

          {pendingOperations.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2">
                Pending Changes ({pendingOperations.length})
              </Typography>
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                These changes will be synchronized when you're back online.
              </Alert>
              <List dense>
                {pendingOperations.slice(0, 3).map((operation) => (
                  <ListItem key={operation.id}>
                    <ListItemText
                      primary={getOperationDescription(operation)}
                      secondary={`Created: ${formatDate(operation.timestamp)}`}
                    />
                    <ListItemSecondaryAction>
                      {getStatusIcon(operation.status)}
                      {(operation.status === 'error' || operation.status === 'requires_action') && (
                        <Tooltip title="Retry">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRetryOperation(operation.id)}
                            disabled={!networkStatus}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDeleteOperation(operation.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {pendingOperations.length > 3 && (
                  <Button 
                    size="small" 
                    sx={{ mt: 1 }} 
                    onClick={() => setShowOfflineDetails(true)}
                  >
                    View all {pendingOperations.length} operations
                  </Button>
                )}
              </List>
            </Box>
          )}
        </>
      )}

      {/* Clear data confirmation dialog */}
      <Dialog open={showConfirmClear} onClose={() => setShowConfirmClear(false)}>
        <DialogTitle>Clear Offline Data?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove all cached boxes, items, and pending changes. Any changes made while offline that have not been synced will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmClear(false)}>Cancel</Button>
          <Button onClick={handleClearData} color="error" variant="contained">
            Clear Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offline details dialog */}
      <Dialog 
        open={showOfflineDetails} 
        onClose={() => setShowOfflineDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Offline Storage Details</DialogTitle>
        <DialogContent>
          {stats && (
            <>
              <Typography variant="h6">Storage Summary</Typography>
              <Typography variant="body2" paragraph>
                {stats.boxCount} boxes and {stats.itemCount} items are stored for offline use.
                Last synchronized: {formatDate(stats.lastSyncTime)}
              </Typography>

              {pendingOperations.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>Pending Operations</Typography>
                  <List>
                    {pendingOperations.map((operation) => (
                      <ListItem key={operation.id}>
                        <ListItemText
                          primary={getOperationDescription(operation)}
                          secondary={
                            <>
                              {`Created: ${formatDate(operation.timestamp)}`}
                              {operation.error && (
                                <Typography color="error" variant="caption" display="block">
                                  Error: {operation.error}
                                </Typography>
                              )}
                              {operation.message && (
                                <Typography variant="caption" display="block">
                                  {operation.message}
                                </Typography>
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          {getStatusIcon(operation.status)}
                          {(operation.status === 'error' || operation.status === 'requires_action') && (
                            <Tooltip title="Retry">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRetryOperation(operation.id)}
                                disabled={!networkStatus}
                                sx={{ ml: 1 }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDeleteOperation(operation.id)}
                              sx={{ ml: 1 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOfflineDetails(false)}>Close</Button>
          {networkStatus && (
            <Button 
              onClick={handleSync} 
              color="primary" 
              startIcon={<SyncIcon />}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OfflineMode;
